import { getLocal, removeLocal, setLocal } from '@/util/storage';

export const SELECTED_POSTAL_LOCATION_STORAGE_KEY = 'selected-postal-location';

export type PostalCodeAddress = {
  queryPostalCode?: string;
  postalCode?: string;
  countryId?: number | string;
  countryCode?: string;
  countryName?: string;
  provinceId?: number | string;
  provinceName?: string;
  cityId?: number | string;
  cityName?: string;
  districtId?: number | string;
  districtName?: string;
  countyId?: number | string;
  countyName?: string;
};

const POSTAL_CODE_AUTOFILL_COUNTRY_CODES = new Set(['DE', 'HU', 'GB', 'UK', 'NL', 'RS', 'ES', 'FR', 'BE']);
const POSTAL_CODE_AUTOFILL_COUNTRY_NAMES = new Set([
  'germany',
  'deutschland',
  '德国',
  'hungary',
  'magyarország',
  '匈牙利',
  'united kingdom',
  'great britain',
  '英国',
  'netherlands',
  'nederland',
  '荷兰',
  'serbia',
  'srbija',
  '塞尔维亚',
  'spain',
  'españa',
  '西班牙',
  'france',
  '法国',
  'belgium',
  'belgië',
  'belgique',
  '比利时',
]);

const firstDefined = (...values: any[]) => values.find(value => value !== undefined && value !== null && value !== '');

const getObjectValue = (value: any) => (value && typeof value === 'object' ? value : {});

const getPostalCodeAddressItems = (response: any): any[] => {
  const value = firstDefined(response?.value, response?.data, response);
  if (Array.isArray(value)) return value;

  const candidates = [
    value?.items,
    value?.list,
    value?.records,
    value?.data,
    value?.addressList,
    value?.postalCodeAddressList,
  ].find(Array.isArray);
  if (candidates) return candidates;

  if (value?.data && value.data !== value) {
    return getPostalCodeAddressItems(value.data);
  }
  return value && typeof value === 'object' ? [value] : [];
};

const getFirstPostalCodeAddress = (response: any) => getPostalCodeAddressItems(response)[0] || null;

/** 国际地址服务可能返回标准字段或层级字段，这里统一成项目现有的国/省/市/区模型。 */
export function normalizePostalCodeAddress(response: any, queryPostalCode?: string): PostalCodeAddress | null {
  const item = getFirstPostalCodeAddress(response);
  if (!item) return null;

  const country = getObjectValue(item.country);
  const province = getObjectValue(firstDefined(item.province, item.secondAddress, item.level2));
  const city = getObjectValue(firstDefined(item.city, item.thirdAddress, item.level3));
  const county = getObjectValue(firstDefined(item.county, item.district, item.fourthAddress, item.level4));
  const stringCountry = typeof item.country === 'string' ? item.country.trim() : '';
  const stringProvince = typeof item.province === 'string' ? item.province.trim() : '';
  const stringCity = typeof item.city === 'string' ? item.city.trim() : '';
  const stringCounty = typeof item.county === 'string' ? item.county.trim() : '';
  const stringCountryCode = /^[a-z]{2,3}$/i.test(stringCountry) ? stringCountry.toUpperCase() : undefined;
  const stringCountryName = stringCountryCode ? undefined : stringCountry;

  const result: PostalCodeAddress = {
    queryPostalCode: queryPostalCode?.trim() || undefined,
    postalCode: firstDefined(item.postalCode, item.postCode, item.zipCode),
    countryId: firstDefined(item.countryId, item.addressLevel1Id, item.firstAddressId, item.level1Id, country.id),
    countryCode: firstDefined(item.countryCode, item.countryCode2, country.code, stringCountryCode),
    countryName: firstDefined(
      item.countryName,
      item.addressLevel1Name,
      item.firstAddressName,
      item.level1Name,
      country.name,
      stringCountryName,
    ),
    provinceId: firstDefined(
      item.provinceId,
      item.addressLevel2Id,
      item.secondAddressId,
      item.level2Id,
      item.addressId2,
      province.id,
    ),
    provinceName: firstDefined(
      item.provinceName,
      item.addressLevel2Name,
      item.secondAddressName,
      item.level2Name,
      item.addressName2,
      province.name,
      stringProvince,
    ),
    cityId: firstDefined(
      item.cityId,
      item.addressLevel3Id,
      item.thirdAddressId,
      item.level3Id,
      item.addressId3,
      city.id,
    ),
    cityName: firstDefined(
      item.cityName,
      item.addressLevel3Name,
      item.thirdAddressName,
      item.level3Name,
      item.addressName3,
      city.name,
      stringCity,
    ),
    districtId: firstDefined(item.districtId, item.addressLevel4Id, item.fourthAddressId, item.level4Id, item.addressId4),
    districtName: firstDefined(item.districtName, item.addressLevel4Name, item.fourthAddressName, item.level4Name, item.addressName4),
    countyId: firstDefined(
      item.countyId,
      item.addressLevel5Id,
      item.fifthAddressId,
      item.level5Id,
      item.addressId5,
      county.id,
    ),
    countyName: firstDefined(
      item.countyName,
      item.districtName,
      item.addressLevel4Name,
      item.fourthAddressName,
      item.level4Name,
      item.addressName4,
      county.name,
      stringCounty,
    ),
  };

  const hasAddressData = Object.entries(result).some(([key, value]) => key !== 'queryPostalCode' && Boolean(value));
  return hasAddressData ? result : null;
}

export function isPostalCodeAutofillCountry(countryCode?: string, countryName?: string) {
  const normalizedCode = String(countryCode || '')
    .trim()
    .toUpperCase();
  // 登录用户国家编码存在时以编码为准，避免不一致的名称把非欧洲用户误判为回填国家。
  if (normalizedCode) return POSTAL_CODE_AUTOFILL_COUNTRY_CODES.has(normalizedCode);
  return POSTAL_CODE_AUTOFILL_COUNTRY_NAMES.has(
    String(countryName || '')
      .trim()
      .toLowerCase(),
  );
}

/** 同一邮编可能跨国家重复，按页面当前一级地址筛选并采用匹配结果中的第一条。 */
export function findMatchingPostalCodeAddress(
  response: any,
  selectedCountryId?: number | string,
  selectedCountryCode?: string,
  queryPostalCode?: string,
): PostalCodeAddress | null {
  const normalizedCountryId = String(selectedCountryId ?? '').trim();
  const normalizedCountryCode = String(selectedCountryCode || '')
    .trim()
    .toUpperCase();

  const addresses = getPostalCodeAddressItems(response)
    .map(item => normalizePostalCodeAddress(item, queryPostalCode))
    .filter((item): item is PostalCodeAddress => Boolean(item));

  return (
    addresses.find(item => {
      const countryIdMatched =
        Boolean(normalizedCountryId) && String(item.countryId ?? '').trim() === normalizedCountryId;
      const countryCodeMatched =
        Boolean(normalizedCountryCode) &&
        String(item.countryCode || '')
          .trim()
          .toUpperCase() === normalizedCountryCode;
      return countryIdMatched || countryCodeMatched;
    }) || null
  );
}

export function formatPostalCodeLocationLabel(location?: PostalCodeAddress | null) {
  if (!location) return '';
  // 邮编定位与地址簿选中地址统一按“国家、邮编、城市”展示；接口未返回邮编时不回显用户输入值。
  return [location.countryName, location.postalCode, location.cityName].filter(Boolean).join(' ');
}

export function getStoredPostalCodeLocation(): PostalCodeAddress | null {
  const stored = getLocal(SELECTED_POSTAL_LOCATION_STORAGE_KEY);
  return stored && typeof stored === 'object' ? stored : null;
}

export function setStoredPostalCodeLocation(location: PostalCodeAddress) {
  setLocal(SELECTED_POSTAL_LOCATION_STORAGE_KEY, location);
}

export function clearStoredPostalCodeLocation() {
  removeLocal(SELECTED_POSTAL_LOCATION_STORAGE_KEY);
}
